import { useState, useMemo, useEffect } from 'react';
import styles from './BillingPage.module.css';
import { BillingContractedPlansCard } from '../components/BillingContractedPlansCard';
import { BillingMonthFilter } from '../components/BillingMonthFilter';
import { BillingConversionFunnelChart } from '../components/BillingConversionFunnelChart';
import { BillingRevenueChart } from '../components/BillingRevenueChart';
import { BillingCashFlowChart } from '../components/BillingCashFlowChart';
import { mapPaymentToRow } from '../utils/payment.utils';
import { getExpirationLabel } from '../utils/expiration.utils';
import {
	billingConversionFunnelConfig,
	billingRevenueConfig,
	billingAbandonedCartsConfig,
	billingSubscriptionsStatusConfig,
	billingContractedPlansListConfig,
	billingCashFlowConfig,
	billingExpiringSubscriptionsConfig,
} from '../constants/billingCharts.config';
import { MONTH_ABBR_PT } from '@practium/shared';
import {
	StatCard,
	StatCardSkeleton,
	PiePercentageChart,
	MetricListCard,
	type StatItemConfig,
} from '@practium/features/dashboard';
import { BuildingIcon, FileCheckIcon, useToast, UsersIcon } from '@practium/ui';
import {
	useGetBilling,
	useGetBillingCashFlow,
	useGetBillingPayments,
	useGetExpiringSubscriptions,
} from '@practium/features/api';

const PAYMENTS_PAGE_SIZE = 10;
const STAT_SKELETON_COUNT = 3;

export const BillingPage = () => {
	const now = new Date();
	const [month, setMonth] = useState(now.getMonth() + 1);
	const [year, setYear] = useState(now.getFullYear());
	const [page, setPage] = useState(1);
	const toast = useToast();

	const { data: docs, isLoading, isError } = useGetBilling({ month, year });
	const {
		data: payments,
		isLoading: isPaymentsLoading,
		isError: isPaymentsError,
	} = useGetBillingPayments({ month, year, page, limit: PAYMENTS_PAGE_SIZE });
	const { data: cashFlow, isLoading: isCashFlowLoading } = useGetBillingCashFlow({ month, year });
	const { data: expiringSubscriptions, isLoading: isExpiringLoading } = useGetExpiringSubscriptions();

	useEffect(() => {
		if (isError) {
			toast.error('Não foi possível carregar o faturamento. Tente novamente.');
		}
	}, [isError]);

	useEffect(() => {
		if (isPaymentsError) {
			toast.error('Não foi possível carregar os pagamentos. Tente novamente.');
		}
	}, [isPaymentsError]);

	const handleMonthChange = (newMonth: number, newYear: number) => {
		setMonth(newMonth);
		setYear(newYear);
		setPage(1);
	};

	const daysInMonth = new Date(year, month, 0).getDate();
	const periodRange = `1-${daysInMonth} ${MONTH_ABBR_PT[month - 1]}, ${year}`;
	const subtitle = `Cadastros de ${periodRange}`;
	const funnelSubtitle = `Dados de ${periodRange}`;
	const abandonedCartsSubtitle = `Abandonos de ${periodRange}`;
	const subscriptionsStatusSubtitle = `Cancelamentos de ${periodRange}`;
	const cashFlowSubtitle = `Entradas e saídas de 1-${cashFlow?.days.length || daysInMonth} ${MONTH_ABBR_PT[month - 1]}, ${year}`;

	const statsConfig = useMemo<StatItemConfig[]>(() => {
		return (docs?.landingPageCards ?? []).map(
			(
				card: { _id?: string; name: string; totalUsers: number; variationPercent: number },
				index: number
			) => {
				const icons = [
					<UsersIcon
						key={`${card._id}-users`}
						style={{ fontSize: '1.5rem', color: 'var(--icon-default)' }}
					/>,
					<BuildingIcon
						key={`${card._id}-building`}
						style={{ fontSize: '1.5rem', color: 'var(--icon-default)' }}
					/>,
					<FileCheckIcon
						key={`${card._id}-file`}
						style={{ fontSize: '1.5rem', color: 'var(--icon-default)' }}
					/>,
				];

				return {
					id: card._id ?? `landing-${index}`,
					label: card.name,
					value: card.totalUsers,
					trendValue: card.variationPercent,
					trendLabel: 'vs mês passado',
					trendDirection: card.variationPercent >= 0 ? 'up' : 'down',
					icon: icons[index % icons.length],
				};
			}
		);
	}, [docs?.landingPageCards]);

	const paymentRows = useMemo(() => (payments?.docs ?? []).map(mapPaymentToRow), [payments?.docs]);

	const contractedPlansListData = useMemo(
		() =>
			[...(docs?.landingPageCards ?? [])]
				.sort((a, b) => b.totalUsers - a.totalUsers)
				.map((card, index) => ({
					id: card._id ?? `plan-${index}`,
					name: card.name,
					value: `${card.totalUsers} Assinaturas`,
				})),
		[docs?.landingPageCards]
	);

	const expiringListData = useMemo(
		() =>
			(expiringSubscriptions ?? []).map((subscription) => {
				const { label, isToday } = getExpirationLabel(
					subscription.expiresIn,
					subscription.waitCancel,
				);

				return {
					id: subscription._id,
					name: subscription.name,
					value: label,
					tone: isToday ? ('danger' as const) : ('default' as const),
				};
			}),
		[expiringSubscriptions]
	);

	return (
		<div className={styles.page}>
			<div className={styles.topRow}>
				{isLoading
					? Array.from({ length: STAT_SKELETON_COUNT }, (_, index) => <StatCardSkeleton key={index} />)
					: statsConfig.map((stat) => (
							<StatCard
								key={stat.id}
								label={stat.label}
								value={stat.value}
								trendValue={stat.trendValue}
								trendLabel={stat.trendLabel}
								trendDirection={stat.trendDirection}
								icon={stat.icon}
							/>
						))}
				<BillingMonthFilter month={month} year={year} onChange={handleMonthChange} />
			</div>

			<div className={styles.chartsGrid}>
				<div className={styles.funnelItem}>
					<BillingConversionFunnelChart
						{...billingConversionFunnelConfig}
						footerText={funnelSubtitle}
						stages={docs?.conversionFunnel?.stages ?? []}
						mainValue={docs?.conversionFunnel?.mainValue ?? '0%'}
						trendValue={docs?.conversionFunnel?.trendValue ?? '0%'}
						trendDirection={docs?.conversionFunnel?.trendDirection ?? 'up'}
						isLoading={isLoading}
					/>
				</div>
				<div className={styles.revenueItem}>
					<BillingRevenueChart
						{...billingRevenueConfig}
						data={docs?.revenueByDay ?? []}
						mainValue={docs?.revenueSummary?.mainValue ?? '0'}
						trendValue={docs?.revenueSummary?.trendValue ?? '0'}
						trendDirection={docs?.revenueSummary?.trendDirection ?? 'up'}
						isLoading={isLoading}
					/>
				</div>
				<div className={styles.pie1Item}>
					<PiePercentageChart
						{...billingAbandonedCartsConfig}
						subtitle={abandonedCartsSubtitle}
						data={docs?.abandonedCarts ?? []}
						isLoading={isLoading}
					/>
				</div>
				<div className={styles.pie2Item}>
					<PiePercentageChart
						{...billingSubscriptionsStatusConfig}
						subtitle={subscriptionsStatusSubtitle}
						data={docs?.subscriptionStatus ?? []}
						isLoading={isLoading}
					/>
				</div>
				<div className={styles.plansItem}>
					<MetricListCard
						{...billingContractedPlansListConfig}
						subtitle={subtitle}
						data={contractedPlansListData}
						isLoading={isLoading}
					/>
				</div>
				<div className={styles.cashFlowItem}>
					<BillingCashFlowChart
						{...billingCashFlowConfig}
						subtitle={cashFlowSubtitle}
						data={cashFlow}
						isLoading={isCashFlowLoading}
					/>
				</div>
				<div className={styles.expiringItem}>
					<MetricListCard
						{...billingExpiringSubscriptionsConfig}
						data={expiringListData}
						showRank={false}
						isLoading={isExpiringLoading}
					/>
				</div>
			</div>

			<BillingContractedPlansCard
				rows={paymentRows}
				subtitle={subtitle}
				page={page}
				totalPages={payments?.totalPages ?? 1}
				onPageChange={setPage}
				onViewReport={() => {}}
				isLoading={isPaymentsLoading}
				isError={isPaymentsError}
			/>
		</div>
	);
};
